import { useCallback } from 'react';
import { t } from 'i18next';
import { isRtlLanguage, type Language } from '@models/i18n/language.model';
import { switchLanguage } from '@i18n/i18n.service';
import { reloadMasterData } from '@master-data/master-data.service';
import { useAppStore, selectLanguage, selectIsLanguageLoading } from '@store/app.store';
import { useMasterDataStore } from '@store/master-data.store';
import { syncUrlToLanguage } from '@utils/language-url.util';
import { logger } from '@utils/logger.util';

interface UseLanguageSwitchReturn {
  currentLanguage: Language;
  switchTo: (language: Language) => Promise<void>;
  isSwitching: boolean;
}

export const useLanguageSwitch = (): UseLanguageSwitchReturn => {
  const currentLanguage = useAppStore(selectLanguage);
  const isSwitching = useAppStore(selectIsLanguageLoading);
  const { setLanguage, setLanguageLoading } = useAppStore();
  const { setMasterData } = useMasterDataStore();

  const switchTo = useCallback(
    async (language: Language): Promise<void> => {
      if (language === currentLanguage) return;

      setLanguageLoading(true);

      try {
        const preferences = useMasterDataStore.getState().masterData?.preferences;

        // 1. Load translations for new language (cached internally by i18next)
        // Translations are required — failure aborts the switch.
        await switchLanguage(language);

        // 2. Master data is best-effort (same as bootstrap). Keep previous master
        // data if reload fails so UI language still commits and avoids mixed state.
        try {
          const masterData = await reloadMasterData(language);
          setMasterData(preferences ? { ...masterData, preferences } : masterData);
        } catch (masterDataError) {
          logger.warn(
            `Master data reload failed during language switch to ${language}; continuing`,
            masterDataError,
          );
        }

        // 3. Persist new language in store + session storage
        setLanguage(language);

        // 4. Update HTML dir/lang attributes — no page reload
        document.documentElement.setAttribute('dir', isRtlLanguage(language) ? 'rtl' : 'ltr');
        document.documentElement.setAttribute('lang', language);

        // 5. Sync URL bar only — no navigation, no remount
        syncUrlToLanguage(language);

        // Update document title with the new language's translation
        document.title = t('OLCI.HeaderPrefix.Text');

        logger.info(`Language switched to: ${language}`);
      } catch (error) {
        logger.error('Language switch failed', error);
        throw error;
      } finally {
        setLanguageLoading(false);
      }
    },
    [currentLanguage, setLanguage, setLanguageLoading, setMasterData],
  );

  return {
    currentLanguage,
    switchTo,
    isSwitching,
  };
};
